import { g } from "../../util/index.ts";

type LeaderRequirement = {
	minStats?: Record<string, number>;
	sortAscending?: true;
	filter?: (p: any) => boolean;

	// For non-numeric stats and other stats that need to have a custom sort.
	sortValue?: (value: any) => number;
};

// This is for league leaders pages and player profile page stat tables
// https://www.basketball-reference.com/about/rate_stat_req.html has some info for basketball, can use as rough guide
// sortAscending can't use lowerIsBetter because some of these (like "tov") are considered "bad" if you lead it
// minStats with multiple values are "OR"ed together
const getLeaderRequirements = (): Record<string, LeaderRequirement> => {
	const basketballPerGameStats = {
		minStats: { gp: 58 },
	};
	const basketballAdvancedStats = {
		minStats: { min: 1500 },
	};
	const basketballAdvancedShootingStats = {
		minStats: { fga: 500 },
	};
	const basketballMinTp = Math.max(
		55 * Math.min(1, g.get("threePointTendencyFactor")),
		12,
	);
	const basketballTpStats = {
		minStats: {
			tp: basketballMinTp,
		},
	};
	const basketballMinFg = 300 * g.get("twoPointAccuracyFactor");
	const basketballFgStats = {
		minStats: { fg: basketballMinFg },
	};
	const basketballFt = {
		minStats: { ft: 125 * g.get("ftAccuracyFactor") },
	};

	return {
		pts: basketballPerGameStats,
		trb: basketballPerGameStats,
		ast: basketballPerGameStats,
		orb: basketballPerGameStats,
		drb: basketballPerGameStats,
		tov: basketballPerGameStats,
		ba: basketballPerGameStats,
		pf: basketballPerGameStats,
		fg: basketballFgStats,
		fga: basketballFgStats,
		fgp: basketballFgStats,
		"2p": basketballFgStats,
		"2pa": basketballFgStats,
		"2pp": basketballFgStats,
		tp: basketballTpStats,
		tpa: basketballTpStats,
		tpp: basketballTpStats,
		ft: basketballFt,
		fta: basketballFt,
		ftp: basketballFt,
		fgAtRim: { minStats: { fgAtRim: basketballMinFg / 5 } },
		fgaAtRim: { minStats: { fgAtRim: basketballMinFg / 5 } },
		fgpAtRim: { minStats: { fgAtRim: basketballMinFg / 5 } },
		fgLowPost: { minStats: { fgLowPost: basketballMinFg / 5 } },
		fgaLowPost: { minStats: { fgLowPost: basketballMinFg / 5 } },
		fgpLowPost: { minStats: { fgLowPost: basketballMinFg / 5 } },
		fgMidRange: { minStats: { fgMidRange: basketballMinFg / 5 } },
		fgaMidRange: { minStats: { fgMidRange: basketballMinFg / 5 } },
		fgpMidRange: { minStats: { fgMidRange: basketballMinFg / 5 } },
		blk: basketballPerGameStats,
		stl: basketballPerGameStats,
		min: basketballPerGameStats,
		per: basketballAdvancedStats,
		ewa: basketballAdvancedStats,
		ws48: basketballAdvancedStats,
		ows: basketballAdvancedStats,
		dws: basketballAdvancedStats,
		ws: basketballAdvancedStats,
		obpm: basketballAdvancedStats,
		dbpm: basketballAdvancedStats,
		bpm: basketballAdvancedStats,
		vorp: basketballAdvancedStats,
		onOff100: basketballAdvancedStats,
		gp: {},
		gs: {},
		dd: {},
		td: {},
		qd: {},
		fxf: {},
		orbp: basketballAdvancedStats,
		drbp: basketballAdvancedStats,
		trbp: basketballAdvancedStats,
		astp: basketballAdvancedStats,
		stlp: basketballAdvancedStats,
		blkp: basketballAdvancedStats,
		tovp: {
			...basketballAdvancedStats,
			sortAscending: true,
		},
		usgp: basketballAdvancedStats,
		pm100: basketballAdvancedStats,
		ortg: basketballAdvancedStats,
		drtg: {
			...basketballAdvancedStats,
			sortAscending: true,
		},
		minMax: {},
		fgMax: {},
		fgaMax: {},
		tpMax: {},
		tpaMax: {},
		"2pMax": {},
		"2paMax": {},
		ftMax: {},
		ftaMax: {},
		orbMax: {},
		drbMax: {},
		trbMax: {},
		astMax: {},
		tovMax: {},
		stlMax: {},
		blkMax: {},
		baMax: {},
		pfMax: {},
		ptsMax: {},
		pmMax: {},
		gmscMax: {},
		tsp: basketballAdvancedShootingStats,
		efg: basketballAdvancedShootingStats,
		tpar: basketballAdvancedShootingStats,
		ftr: basketballAdvancedShootingStats,
	};
};

export default getLeaderRequirements;
